/**
 * Client-side mirror of the Wint.Salary schedule validation rules.
 *
 * The server requires scheduledMinutes == standardWorkTime × workRate × weeks with exact
 * decimal equality. StandardWorkTime (Ordinarie arbetstid, the implied full-time norm) is
 * editable, but pay and vacation accrual follow workRate — so changing the norm while the
 * rate stays put is warned about in the form. Baselines come from the persisted
 * currentSchedule, falling back to the futureSchedule when only future schedules exist;
 * with no persisted schedule the comparisons are skipped (creation is unaffected).
 *
 * Pure module: no React or app imports, so the rules stay portable toward Wint.Client.Shared.
 */

// Far below the 0.01 h input granularity (0.6 min), far above float noise. Values that squeak
// past this are still rejected by the server's exact decimal comparison and surfaced via the
// error-code fallback, so the epsilon can only under-block, never over-block.
export const SCHEDULE_WORK_TIME_EPSILON_MINUTES = 0.001;

// Same idea for workRate (decimal 0-1): far below the 0.01 % input granularity (0.0001).
export const SCHEDULE_WORK_RATE_EPSILON = 0.000001;

export interface ScheduleWeekHours {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

export const getStandardWorkTimeBaseline = (schedule?: {
  currentSchedule?: { standardWorkTime?: number };
  futureSchedule?: { standardWorkTime?: number };
}): number | undefined =>
  schedule?.currentSchedule?.standardWorkTime ?? schedule?.futureSchedule?.standardWorkTime;

export const getWorkRateBaseline = (schedule?: {
  currentSchedule?: { workRate?: number };
  futureSchedule?: { workRate?: number };
}): number | undefined => schedule?.currentSchedule?.workRate ?? schedule?.futureSchedule?.workRate;

// Total scheduled hours across all weeks. Shared by the validation below and the grid's
// entered-time/difference display so the formula only exists once.
export const sumScheduleWeekHours = (weeks: ScheduleWeekHours[]): number =>
  weeks.reduce(
    (total, week) =>
      total + week.monday + week.tuesday + week.wednesday + week.thursday + week.friday + week.saturday + week.sunday,
    0
  );

// Expected total hours: usualWorkHours × workRate × weeks, with the DTO defaults.
export const getExpectedTotalHours = (
  weekCount: number,
  usualWorkHours: number | undefined,
  employmentRate: number | undefined
): number => (usualWorkHours ?? 40) * ((employmentRate ?? 100) / 100) * weekCount;

// Mirrors the save path exactly: the DTO sends standardWorkTime = (usualWorkHours ?? 40) * 60.
export const isStandardWorkTimeUnchanged = (usualWorkHours: number | undefined, baselineMinutes: number): boolean =>
  Math.abs((usualWorkHours ?? 40) * 60 - baselineMinutes) < SCHEDULE_WORK_TIME_EPSILON_MINUTES;

// Mirrors the save path exactly: the DTO sends workRate = (employmentRate ?? 100) / 100.
export const isWorkRateUnchanged = (employmentRate: number | undefined, baselineWorkRate: number): boolean =>
  Math.abs((employmentRate ?? 100) / 100 - baselineWorkRate) < SCHEDULE_WORK_RATE_EPSILON;

// Pay and vacation accrual follow workRate, so a norm change with the rate left untouched is
// the state the form warns about. Skipped when nothing is persisted (creation).
export const standardWorkTimeChangedButWorkRateUnchanged = (
  usualWorkHours: number | undefined,
  employmentRate: number | undefined,
  baselineStandardWorkTimeMinutes: number | undefined,
  baselineWorkRate: number | undefined
): boolean =>
  baselineStandardWorkTimeMinutes !== undefined &&
  baselineWorkRate !== undefined &&
  !isStandardWorkTimeUnchanged(usualWorkHours, baselineStandardWorkTimeMinutes) &&
  isWorkRateUnchanged(employmentRate, baselineWorkRate);

// Mirrors the server equation scheduledMinutes == standardWorkTime × workRate × weeks, with the
// DTO conversions workRate = (employmentRate ?? 100) / 100 and grid hours × 60.
export const scheduleMatchesExpectedWorkTime = (
  weeks: ScheduleWeekHours[],
  usualWorkHours: number | undefined,
  employmentRate: number | undefined
): boolean => {
  const scheduledMinutes = sumScheduleWeekHours(weeks) * 60;
  const expectedMinutes = getExpectedTotalHours(weeks.length, usualWorkHours, employmentRate) * 60;
  return Math.abs(scheduledMinutes - expectedMinutes) < SCHEDULE_WORK_TIME_EPSILON_MINUTES;
};
